"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  Bell,
  Search,
  Settings,
  HelpCircle,
  ChevronDown,
  LogOut,
  User,
  CreditCard,
  Shield,
  X,
  Check,
  AlertTriangle,
  Info,
  Bot,
} from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { useStoreStore } from "@/stores/store-store";
import {
  getNotifications,
  getNotificationsStreamUrl,
  markNotificationAsRead,
  type Notification as ApiNotification,
} from "@/lib/api/dashboard-api";
import { getAuthToken } from "@/lib/api/http-client";

// UI shape for notification list (mapped from API)
interface Notification {
  id: string;
  type: "info" | "warning" | "success" | "ai";
  title: string;
  description: string;
  time: string;
  read: boolean;
}

function formatRelativeTime(createdAt: string): string {
  try {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Baru saja";
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    if (diffDays === 1) return "1 hari lalu";
    if (diffDays < 7) return `${diffDays} hari lalu`;
    return date.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

function mapApiNotificationToUI(api: ApiNotification): Notification {
  const typeMap: Record<string, Notification["type"]> = {
    warning: "warning",
    error: "warning",
    critical: "warning",
    info: "info",
  };
  return {
    id: api.id,
    type: typeMap[api.type] ?? "info",
    title: api.title,
    description: api.message,
    time: formatRelativeTime(api.created_at),
    read: api.is_read,
  };
}

function roleLabel(role: string): string {
  const labels: Record<string, string> = {
    owner: "Owner",
    admin: "Admin",
    marketing: "Marketing",
    cs_agent: "CS Agent",
    finance: "Finance",
    developer: "Developer",
  };
  return labels[role] ?? role;
}

/* SUBSC-34 — `planLabel()` dihapus.
   Ia tabel slug→label di frontend: memetakan `business` -> "Business" lalu
   dirender sebagai "Business Plan". Dua alasan ia salah, dan yang kedua yang
   membuatnya tidak bisa ditambal:

   1. Ia sumber KEDUA untuk fakta yang sama dengan `plans.name` di backend.
   2. Ia mengandaikan nama paket selalu = tier-nya dikapitalkan. Begitu ada
      paket bernama "Business Pro" atau "Growth", pemetaan ini mengarang nama
      yang tidak pernah ada — dan tidak ada yang akan tahu, sebab hasilnya
      tetap terlihat seperti nama paket.

   Backend sekarang mengirim `plan_name` di StoreDTO (nol query tambahan,
   JOIN yang sama dengan tier), jadi labelnya datang dari satu tempat. */

const notifIcon = (type: Notification["type"]) => {
  switch (type) {
    case "warning":
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case "success":
      return <Check className="w-4 h-4 text-green-500" />;
    case "ai":
      return <Bot className="w-4 h-4 text-purple-500" />;
    default:
      return <Info className="w-4 h-4 text-blue-500" />;
  }
};

const notifBg = (type: Notification["type"]) => {
  switch (type) {
    case "warning":
      return "bg-amber-100";
    case "success":
      return "bg-green-100";
    case "ai":
      return "bg-purple-100";
    default:
      return "bg-blue-100";
  }
};

export function TopNavbar() {
  const router = useRouter();
  const { user, logout } = useAuthStore();
  const { activeStoreId, getActiveStore } = useStoreStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const previousUnreadCountRef = useRef<number>(0);
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
  const isInitialMountRef = useRef(true);
  const [useSSE, setUseSSE] = useState(true);

  const activeStore = getActiveStore();
  /* SUBSC-34 — TIDAK ada default.
     Sebelumnya `?? user?.stores?.[0]?.tier ?? "business"`: store tanpa data
     paket menampilkan "Business Plan". Itu karangan yang sama dengan literal
     di sidebar, cuma dengan dua `??` di depannya sehingga terbaca seperti
     penanganan berlapis yang hati-hati — reviewer melihat rantai fallback dan
     menyimpulkan seseorang sudah memikirkannya.

     Keputusan sebenarnya cuma ada di kata terakhir: kalau tidak tahu paketnya,
     tebak yang berbayar. Default yang benar bukan tier lain — melainkan tidak
     menampilkan apa pun. */
  const planName = activeStore?.planName;

  const unreadCount = notifications.filter((n) => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    if (!activeStoreId) {
      setNotifications([]);
      previousUnreadCountRef.current = 0;
      return;
    }
    setNotificationsLoading(true);
    try {
      const list = await getNotifications(activeStoreId, false);
      const mapped = list.map(mapApiNotificationToUI);
      const newUnreadCount = mapped.filter((n) => !n.read).length;
      const prev = previousUnreadCountRef.current;
      setNotifications(mapped);
      previousUnreadCountRef.current = newUnreadCount;
      // Play sound when new notifications appear (skip on initial load)
      if (!isInitialMountRef.current && newUnreadCount > prev && document.visibilityState === "visible") {
        try {
          if (!notificationSoundRef.current) {
            notificationSoundRef.current = new Audio("/sounds/notification.mp3");
          }
          notificationSoundRef.current.play().catch(() => {});
        } catch {
          // ignore
        }
      }
      isInitialMountRef.current = false;
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [activeStoreId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // When switching store: reset refs so first fetch for new store doesn't play sound, and try SSE again
  useEffect(() => {
    previousUnreadCountRef.current = 0;
    isInitialMountRef.current = true;
    setUseSSE(true);
  }, [activeStoreId]);

  // SSE: real-time notifications stream (fallback to polling on error)
  useEffect(() => {
    if (!activeStoreId || !getAuthToken()) return;
    if (!useSSE) return;
    const url = getNotificationsStreamUrl(activeStoreId);
    const es = new EventSource(url);
    es.onmessage = () => {
      fetchNotifications();
    };
    es.onerror = () => {
      es.close();
      setUseSSE(false);
    };
    return () => {
      es.close();
    };
  }, [activeStoreId, useSSE, fetchNotifications]);

  // Polling fallback when SSE is disabled or failed (every 45 seconds)
  useEffect(() => {
    if (!activeStoreId || useSSE) return;
    const interval = setInterval(fetchNotifications, 45_000);
    return () => clearInterval(interval);
  }, [activeStoreId, useSSE, fetchNotifications]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAllRead = async () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await Promise.all(unreadIds.map((id) => markNotificationAsRead(id)));
    } catch {
      fetchNotifications();
    }
  };

  const markRead = async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await markNotificationAsRead(id);
    } catch {
      // Revert on error
      fetchNotifications();
    }
  };

  const handleLogout = () => {
    setShowProfile(false);
    logout();
    router.push("/login");
  };

  const displayName = user?.name ?? "Guest";
  const displayEmail = user?.email ?? "";
  const displayRole = user?.role ? roleLabel(user.role) : "";
  const avatarInitials = (user?.avatar || displayName.slice(0, 2).toUpperCase()).slice(0, 2);

  return (
    <nav className="sticky top-0 z-40 w-full bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
        {/* CENTER: Search */}
        <div className="flex-1 max-w-xs md:max-w-sm lg:max-w-md">
          <div
            className={`relative flex items-center transition-all duration-200 ${searchFocused ? "ring-2 ring-blue-100 rounded-lg" : ""}`}
          >
            <Search className="w-4 h-4 absolute left-3 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search contacts, campaigns, conversations…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-blue-300 transition-all duration-150"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* RIGHT: Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* <button
            title="Help"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-150"
          >
            <HelpCircle className="w-5 h-5" />
          </button>

          <button
            title="Settings"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-150"
          >
            <Settings className="w-5 h-5" />
          </button> */}

          {/* Notification Bell */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => {
                setShowNotifications((v) => !v);
                setShowProfile(false);
              }}
              className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-150 ${showNotifications ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"}`}
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden z-50">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">Notifications</span>
                    {unreadCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                  {notificationsLoading ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">Memuat notifikasi…</div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">Tidak ada notifikasi</div>
                  ) : (
                    notifications.map((notif) => (
                      <button
                        key={notif.id}
                        onClick={() => markRead(notif.id)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors duration-100 hover:bg-gray-50 ${!notif.read ? "bg-blue-50/40" : ""}`}
                      >
                        <div
                          className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${notifBg(notif.type)}`}
                        >
                          {notifIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p
                              className={`text-sm truncate ${!notif.read ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}
                            >
                              {notif.title}
                            </p>
                            {!notif.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.description}</p>
                          <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                  <button className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors w-full text-center">
                    View all notifications →
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Profile */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => {
                setShowProfile((v) => !v);
                setShowNotifications(false);
              }}
              className={`flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg transition-colors duration-150 ${showProfile ? "bg-gray-100" : "hover:bg-gray-100"}`}
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{avatarInitials}</span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-semibold text-gray-900 leading-tight">{displayName}</p>
                <p className="text-[10px] text-gray-400 leading-tight">{displayRole}</p>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 text-gray-400 hidden md:block transition-transform duration-150 ${showProfile ? "rotate-180" : ""}`}
              />
            </button>

            {showProfile && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                  {displayEmail && <p className="text-xs text-gray-500 mt-0.5">{displayEmail}</p>}
                  {planName && (
                    <span
                      data-testid="navbar-plan-name"
                      className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-full uppercase tracking-wide"
                    >
                      {planName}
                    </span>
                  )}
                </div>

                <div className="py-1">
                  {/* Profile menu links (e.g. My Profile, Billing) can be re-enabled here when needed */}
                </div>

                <div className="border-t border-gray-100 py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-100"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
